// Theme-skin registry.
//
// A skin = a colour pack (CSS token block in styles.css) + a bespoke PromoteHero
// scene (a module under this dir, built on scene/engine). The base :root IS the
// fantasy palette; every other skin adds a `:root[data-skin='<id>']` token block.
// Add a skin: create its module, import it, add one line to SKINS, extend SkinId.
import { useEffect, useState } from 'react';
import { fantasy } from './fantasy';
import { cyberpunk } from './cyberpunk';
import { zen } from './zen';
import { reef } from './reef';
import { solarpunk } from './solarpunk';
import type { SkinDef } from './types';

// Re-export the shared scene types/helpers so consumers import them from './skins'.
export type { HeroState, PromoteSceneProps } from '../scene/engine';
export { heroCenterPct } from '../scene/engine';
export type { SkinDef, SkinLines } from './types';

export type SkinId = 'fantasy' | 'cyberpunk' | 'zen' | 'reef' | 'solarpunk';

export const SKINS: Record<SkinId, SkinDef> = {
  fantasy,
  cyberpunk,
  zen,
  reef,
  solarpunk,
};

/** Order shown in the theme picker. */
export const SKIN_ORDER: SkinId[] = ['fantasy', 'cyberpunk', 'zen', 'reef', 'solarpunk'];

const SKIN_KEY = 'console-skin';
function isSkin(v: string | null): v is SkinId { return v != null && v in SKINS; }

// Active-skin state: persisted, and mirrored onto <html data-skin> so the CSS token
// blocks apply app-wide. `setSkin` powers the theme picker.
export function useSkin() {
  const [skin, setSkin] = useState<SkinId>(() => {
    try { const s = localStorage.getItem(SKIN_KEY); if (isSkin(s)) return s; } catch { /* no storage */ }
    return 'fantasy';
  });
  useEffect(() => {
    document.documentElement.dataset.skin = skin;
    try { localStorage.setItem(SKIN_KEY, skin); } catch { /* ignore */ }
  }, [skin]);
  return { skin, setSkin };
}
